import { fromLonLat, toLonLat, transformExtent } from "ol/proj";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import Point from "ol/geom/Point";
import { containsCoordinate } from "ol/extent";
import mapArrow from "../../assets/images/map-arrow.png";

import {
  MAP_RASTER_URLS,
  MAP_VECTOR_URLS,
  MAP_USE_RASTER
} from "../data/constants";

import * as markers from "./markers";
import tinycolor2 from "tinycolor2";

const svgToInline = svgString => `data:image/svg+xml;base64,${btoa(svgString)}`;
const hexToRgb = hex =>
  hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => "#" + r + r + g + g + b + b
    )
    .substring(1)
    .match(/.{2}/g)
    .map(x => parseInt(x, 16));

const styleCache = {};

const clusterMarkerBaseStyles = {
  small: new Style({
    image: new Icon({
      src: svgToInline(markers.cluster("small")),
      scale: 0.052
    }),
    text: new Text({
      text: "",
      offsetY: 1,
      offsetX: 0.5,
      scale: 1.2,
      fill: new Fill({
        color: "#fff"
      })
    })
  }),
  medium: new Style({
    image: new Icon({
      src: svgToInline(markers.cluster("medium")),
      scale: 0.069
    }),
    text: new Text({
      text: "",
      offsetY: 1.25,
      offsetX: 0.25,
      scale: 1.2,
      fill: new Fill({
        color: "#fff"
      })
    })
  }),
  large: new Style({
    image: new Icon({
      src: svgToInline(markers.cluster("large")),
      scale: 0.086
    }),
    text: new Text({
      text: "",
      offsetY: 1.5,
      offsetX: 0.5,
      scale: 1.2,
      fill: new Fill({
        color: "#fff"
      })
    })
  })
};

export const featureStyles = {
  getClusterMarkerStyle(
    clusterFeatureSize,
    hasUnscheduled,
    outOfClusterAndZoomLevel,
    zIndexSalt
  ) {
    const zIndex = 11 + clusterFeatureSize / 50000 + zIndexSalt;
    const styleCacheId = `marker:cluster:${clusterFeatureSize}:${
      hasUnscheduled ? "has-unscheduled" : "no-unscheduled"
    }:${outOfClusterAndZoomLevel ? zIndex : ""}`;
    // If not in cache, cache it and the return the cached value
    if (!styleCache[styleCacheId]) {
      let newStyle = clusterMarkerBaseStyles.small.clone();
      if (clusterFeatureSize >= 10 && clusterFeatureSize < 100) {
        newStyle = clusterMarkerBaseStyles.medium.clone();
      } else if (clusterFeatureSize >= 100) {
        newStyle = clusterMarkerBaseStyles.large.clone();
      }
      newStyle
        .getText()
        .setText(
          `${clusterFeatureSize.toString()}${hasUnscheduled ? "*" : ""}`
        );
      if (outOfClusterAndZoomLevel) {
        newStyle.setZIndex(zIndex);
      }
      styleCache[styleCacheId] = newStyle;
    }
    return styleCache[styleCacheId];
  },
  getStopMarkerStyles(marker, state = "normal") {
    const markerType = marker.get("markerType");
    if (markerType === "unscheduled") {
      const styleCacheId = `marker:unscheduled:${state}`;
      if (!styleCache[styleCacheId]) {
        let scale = 0.842;
        if (state === "hovered") {
          scale = 1;
        } else if (state === "edit") {
          scale = 1.3;
        }
        styleCache[styleCacheId] = new Style({
          zIndex: 11,
          image: new Icon({
            src: svgToInline(markers.unscheduledStop()),
            size: [38, 38],
            offset: [0, 0],
            opacity: 1,
            scale
          })
        });
      }
      return styleCache[styleCacheId];
    } else if (markerType === "stop") {
      const { color, type, status, index } = marker.get("stopData");
      const isStartEnd = type === "start" || type === "end";
      const styleCacheId = `marker:stop:${
        !isStartEnd ? "job" : type
      }:${state}:${color}${!isStartEnd ? `:${status}` : ""}${
        isStartEnd || status === "skipped" ? "" : `:${index}`
      }`;
      if (!styleCache[styleCacheId]) {
        const baseStyleCacheId = `marker:stop:base:${
          !isStartEnd ? "job" : type
        }:${state}:${color}:${status}`;
        if (!styleCache[baseStyleCacheId]) {
          if (isStartEnd) {
            styleCache[baseStyleCacheId] = new Style({
              zIndex: state === "hovered" ? 501 : undefined,
              image: new Icon({
                src: svgToInline(
                  markers.stop(
                    type,
                    state === "hovered"
                      ? tinycolor2(color).saturate(50).toString()
                      : color,
                    status
                  )
                ),
                size: [35, 60],
                anchor: [17.5, 40],
                anchorXUnits: "pixels",
                anchorYUnits: "pixels",
                scale: state === "hovered" ? 1.07 : 0.97
              })
            });
          } else {
            if (state === "edit") {
              styleCache[baseStyleCacheId] = new Style({
                image: new Icon({
                  src: svgToInline(markers.stopEdit(color)),
                  size: [36, 66],
                  anchor: [18, 51.5],
                  anchorXUnits: "pixels",
                  anchorYUnits: "pixels",
                  scale: 1
                }),
                text: new Text({
                  text: "",
                  font: "600 9px Roboto",
                  offsetY: -31.5,
                  offsetX: -0.6,
                  scale: 1.2,
                  fill: new Fill({
                    color: "#000"
                  })
                })
              });
            } else {
              styleCache[baseStyleCacheId] = new Style({
                zIndex: state === "hovered" ? 501 : undefined,
                image: new Icon({
                  src: svgToInline(
                    markers.stop(
                      type,
                      state === "hovered"
                        ? tinycolor2(color).saturate(50).toString()
                        : color,
                      status
                    )
                  ),
                  size: [52, 76],
                  anchor: [27, 64],
                  anchorXUnits: "pixels",
                  anchorYUnits: "pixels",
                  scale: state === "hovered" ? 0.71 : 0.61
                }),
                text: new Text({
                  text: "",
                  font: "600 9px Roboto",
                  placement: "line",
                  justify: "center",
                  offsetY: state === "hovered" ? -29 : -25,
                  scale: 1.2,
                  fill: new Fill({
                    color: status === "finished" ? "#fff" : "#000"
                  })
                })
              });
            }
          }
        }
        const newStyle = styleCache[baseStyleCacheId].clone();
        if (state !== "hovered" || state !== "edit") {
          newStyle.setZIndex(marker.get("zIndex"));
        }
        if (!isStartEnd) {
          newStyle.getText().setText(status === "skipped" ? "X" : `${index}`);
        }
        styleCache[styleCacheId] = newStyle;
      }
      return styleCache[styleCacheId];
    }
  },
  getStopMarkerLabelStyle(label, markerType = "default") {
    const styleCacheId = `label:stop-marker:${markerType}:${label}`;
    if (!styleCache[styleCacheId]) {
      const baseStyleCacheId = `label:stop-marker:${markerType}:base`;
      if (!styleCache[baseStyleCacheId]) {
        styleCache[baseStyleCacheId] = new Style({
          text: new Text({
            font: "13px sans-serif",
            fill: new Fill({
              color: "#5f6368"
            }),
            stroke: new Stroke({
              color: "#FFFFFFFA",
              width: 5
            }),
            textAlign: "left",
            offsetX: markerType === "stop" ? 27 : 24,
            offsetY: markerType === "stop" ? -22 : 1,
            text: ""
          })
        });
      }
      const newStyle = styleCache[baseStyleCacheId].clone();
      newStyle.getText().setText(`${label}`);
      styleCache[styleCacheId] = newStyle;
    }
    return styleCache[styleCacheId];
  },
  getClusterSurfaceStyle() {
    const styleCacheId = "cluster:surface";
    if (!styleCache[styleCacheId]) {
      styleCache[styleCacheId] = new Style({
        stroke: new Stroke({
          color: "#3388FF",
          width: 3
        }),
        fill: new Fill({
          color: "rgba(51,136,255, 0.1)"
        })
      });
    }
    return styleCache[styleCacheId];
  },
  getRoutePolylinesStyle(color) {
    const styleCacheId = `polyline:${color}`;
    if (!styleCache[styleCacheId]) {
      const rgb = hexToRgb(color);
      styleCache[styleCacheId] = new Style({
        stroke: new Stroke({
          color: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.8)`,
          width: 3
        })
      });
    }
    return styleCache[styleCacheId];
  },
  getRoutePolylineDirectionMarker(zoomLevel, color) {
    const styleCacheId = `polyline:direction-arrow:${zoomLevel}:${color}`;
    if (!styleCache[styleCacheId]) {
      styleCache[styleCacheId] = new Style({
        image: new Icon({
          src: mapArrow,
          anchor: [0.5, 0.5],
          scale: zoomLevel < 10 ? 0.05 : 0.06,
          color
        })
      });
    }
    return styleCache[styleCacheId];
  },
  getSnapToRoadLineStyle() {
    const styleCacheId = "line:snap-to-road";
    if (!styleCache[styleCacheId]) {
      styleCache[styleCacheId] = new Style({
        stroke: new Stroke({
          color: "#000",
          width: 4,
          lineDash: [4, 8]
        })
      });
    }
    return styleCache[styleCacheId];
  },
  getParkingPointStyle() {
    const styleCacheId = "marker:parking";
    if (!styleCache[styleCacheId]) {
      styleCache[styleCacheId] = new Style({
        image: new Icon({
          src: svgToInline(markers.parking()),
          size: [30, 51],
          anchor: [15, 34],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          scale: 0.9
        })
      });
    }
    return styleCache[styleCacheId];
  },
  getDriverMarkerStyle(color) {
    const styleCacheId = `marker:driver:${color}`;
    if (!styleCache[styleCacheId]) {
      styleCache[styleCacheId] = new Style({
        image: new Icon({
          src: svgToInline(markers.driver(color)),
          size: [40, 56],
          anchor: [20, 51],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          scale: 0.9
        })
      });
    }
    return styleCache[styleCacheId];
  }
};

export const layerStyles = {
  getMarkerLayerFeatureStyle(
    clusterFeature,
    state = "normal",
    outOfClusterAndZoomLevel = false
  ) {
    const clusterFeatureFeatures = clusterFeature.get("features");
    const clusterFeatureSize = clusterFeatureFeatures.length;
    if (clusterFeatureSize === 1) {
      return featureStyles.getStopMarkerStyles(
        clusterFeatureFeatures[0],
        state
      );
    }
    let hasUnscheduled = false;
    let zIndexSalt = 1;
    if (outOfClusterAndZoomLevel) {
      hasUnscheduled = clusterFeatureFeatures.some(
        feature => feature.get("markerType") === "unscheduled"
      );
      const coordinates = clusterFeature.getGeometry().getCoordinates();
      zIndexSalt = (coordinates[0] + coordinates[1]) / 1000000;
    }
    return featureStyles.getClusterMarkerStyle(
      clusterFeatureSize,
      hasUnscheduled,
      outOfClusterAndZoomLevel,
      zIndexSalt
    );
  },
  getSelectClusterFeatureStyle(clusterFeature) {
    if (clusterFeature.get("features")) {
      return layerStyles.getMarkerLayerFeatureStyle(clusterFeature);
    } else {
      const styleCacheId = `select-cluster:line`;
      if (!styleCache[styleCacheId]) {
        styleCache[styleCacheId] = new Style({
          stroke: new Stroke({
            color: "#777777AA",
            width: 1
          })
        });
      }
      return styleCache[styleCacheId];
    }
  },
  getShowingRouteLayerFeatureStyle(feature) {
    if (feature.getGeometry() instanceof Point) {
      return featureStyles.getStopMarkerStyles(feature);
    } else {
      const color = feature.get("color");
      const styleCacheId = `polyline:showing:${color}`;
      if (!styleCache[styleCacheId]) {
        const newStyle = featureStyles.getRoutePolylinesStyle(color).clone();
        newStyle.getStroke().setWidth(5);
        styleCache[styleCacheId] = newStyle;
      }
      return styleCache[styleCacheId];
    }
  }
};

// Source: https://gis.stackexchange.com/a/306979
export const lineString = {
  splitLineString(geometry, minSegmentLength, options) {
    function calculatePointsDistance(coordinateOne, coordinateTwo) {
      const dx = coordinateOne[0] - coordinateTwo[0];
      const dy = coordinateOne[1] - coordinateTwo[1];
      return Math.sqrt(dx * dx + dy * dy);
    }

    function calculateSplitPointCoords(
      startNode,
      nextNode,
      distanceBetweenNodes,
      distanceToSplitPoint
    ) {
      const d = distanceToSplitPoint / distanceBetweenNodes;
      const x = nextNode[0] + (startNode[0] - nextNode[0]) * d;
      const y = nextNode[1] + (startNode[1] - nextNode[1]) * d;
      return [x, y];
    }

    function calculateAngle(startNode, nextNode, alwaysUp) {
      const x = startNode[0] - nextNode[0];
      const y = startNode[1] - nextNode[1];
      let angle = Math.atan(x / y);
      if (!alwaysUp) {
        angle = y > 0 ? angle + Math.PI : x < 0 ? angle + Math.PI * 2 : angle;
      }
      return angle;
    }

    const splitPoints = [];
    const coordinates = geometry.getCoordinates();
    let coordinatesIndex = 0;
    let startPoint = coordinates[coordinatesIndex];
    let nextPoint = coordinates[coordinatesIndex + 1];
    let angle =
      options.vertices ||
      calculateAngle(startPoint, nextPoint, options.alwaysUp);

    let n = Math.ceil(geometry.getLength() / minSegmentLength);
    let segmentLength = geometry.getLength() / n;
    let midPoints = options.midPoints && !options.vertices;
    let currentSegmentLength = midPoints ? segmentLength / 2 : segmentLength;

    for (let i = 0; i <= n; i++) {
      let distanceBetweenPoints = calculatePointsDistance(
        startPoint,
        nextPoint
      );
      currentSegmentLength += distanceBetweenPoints;

      if (currentSegmentLength < segmentLength) {
        coordinatesIndex++;
        if (coordinatesIndex < coordinates.length - 1) {
          startPoint = coordinates[coordinatesIndex];
          nextPoint = coordinates[coordinatesIndex + 1];
          angle =
            options.vertices ||
            calculateAngle(startPoint, nextPoint, options.alwaysUp);
          if (
            options.vertices &&
            (!options.extent || containsCoordinate(options.extent, startPoint))
          ) {
            splitPoints.push(startPoint);
          }
          i--;
          continue;
        } else {
          if (!midPoints) {
            let splitPointCoords = nextPoint;
            if (
              !options.extent ||
              containsCoordinate(options.extent, splitPointCoords)
            ) {
              if (!options.vertices) {
                splitPointCoords.push(angle);
              }
              splitPoints.push(splitPointCoords);
            }
          }
          break;
        }
      } else {
        let distanceToSplitPoint = currentSegmentLength - segmentLength;
        let splitPointCoords = calculateSplitPointCoords(
          startPoint,
          nextPoint,
          distanceBetweenPoints,
          distanceToSplitPoint
        );
        startPoint = splitPointCoords.slice();
        if (
          !options.extent ||
          containsCoordinate(options.extent, splitPointCoords)
        ) {
          if (!options.vertices) {
            splitPointCoords.push(angle);
          }
          splitPoints.push(splitPointCoords);
        }
        currentSegmentLength = 0;
      }
    }

    return splitPoints;
  }
};

export const features = {
  findMaxByZIndex(features) {
    const zIndexes = features.map(feature => feature.get("zIndex") || 10.1);
    return features[zIndexes.indexOf(Math.max(...zIndexes))];
  }
};

export const mapTiles = {
  getUrl(plan, tilesDark, pixelRatio) {
    if (MAP_USE_RASTER) {
      return tilesDark
        ? MAP_RASTER_URLS[`${plan.default_map_tile_provider}_DARK`].replace(
            "{r}",
            `@${pixelRatio}x`
          )
        : MAP_RASTER_URLS[`${plan.default_map_tile_provider}`].replace(
            "{r}",
            `@${pixelRatio}x`
          );
    } else {
      return tilesDark
        ? MAP_VECTOR_URLS[`${plan.default_map_tile_provider}_DARK`]
        : MAP_VECTOR_URLS[`${plan.default_map_tile_provider}`];
    }
  }
};

export const pointArray = {
  lonLatToXY(pointArray) {
    return fromLonLat(pointArray);
  },
  xyToLonLat(XYArray) {
    return toLonLat(XYArray);
  },
  toLeafletObject(pointArray) {
    return {
      lat: pointArray[1],
      lng: pointArray[0]
    };
  }
};

export const extentArray = {
  lonLatToXY(extentArray) {
    return transformExtent(extentArray, "EPSG:4326", "EPSG:3857");
  },
  xyToLonLat(extentArray) {
    return transformExtent(extentArray, "EPSG:3857", "EPSG:4326");
  },
  toLeafletObject(extentArray) {
    return {
      _northEast: {
        lat: extentArray[2],
        lng: extentArray[3]
      },
      _southWest: {
        lat: extentArray[1],
        lng: extentArray[0]
      }
    };
  }
};
